import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { RadioButton } from 'primereact/radiobutton';
import { InputText } from 'primereact/inputtext';

const getFlagClassNameForDialog = (languageCode) => {
  if (!languageCode) return 'fi fi-xx';
  const codeToFlagSuffixMap = {
    en: 'us',
    tr: 'tr',
    de: 'de',
    fr: 'fr',
    it: 'it',
    es: 'es',
    ro: 'ro',
  };
  const lower = languageCode.toLowerCase();
  const suffix = codeToFlagSuffixMap[lower];
  return suffix ? `fi fi-${suffix}` : `fi fi-${lower}`;
};

const SUPPORTED_LANGUAGES = [
  { id: 1, code: 'en', name: 'English' },
  { id: 2, code: 'tr', name: 'Türkçe' },
  { id: 3, code: 'fr', name: 'Français' },
  { id: 4, code: 'de', name: 'Deutsch' },
  { id: 5, code: 'it', name: 'Italiano' },
  { id: 6, code: 'ro', name: 'Română' },
].sort((a, b) => {
  if (a.code === 'en') return -1;
  if (b.code === 'en') return 1;
  if (a.code === 'tr') return -1;
  if (b.code === 'tr') return 1;
  return a.name.localeCompare(b.name);
});

const AnnouncementTypeForm = ({
  visible,
  onHide,
  onSubmit,
  initialData,
  loading,
  handleDelete,
}) => {
  const supportedLanguages = SUPPORTED_LANGUAGES;
  const { th } = useLanguage();

  const TYPE_OPTIONS = useMemo(
    () => [
      { id: 132, label: th('announcements.typeLabels.spareParts') },
      { id: 131, label: th('announcements.typeLabels.warranty') },
      { id: 130, label: th('announcements.typeLabels.afterSales') },
      { id: 129, label: th('announcements.typeLabels.marketing') },
      { id: 128, label: th('announcements.typeLabels.sales') },
    ],
    [th]
  );

  const blankTranslations = () =>
    supportedLanguages.map((lang) => ({ text: '', languageId: lang.id }));

  const [formData, setFormData] = useState({
    id: null,
    translationText: '',
    announcementType: '',
    createdDate: null,
    createdName: '',
    type: null,
    isActive: 1,
    translations: blankTranslations(),
  });

  const toast = useRef(null);
  const [localSubmitted, setLocalSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!visible) return;

    const backendTranslations = (initialData && initialData.translations) || [];
    const mergedTranslations = supportedLanguages.map((lang) => {
      const existing = backendTranslations.find((t) => t.languageId === lang.id);
      return existing ? { ...existing } : { languageId: lang.id, text: '' };
    });

    const fixedCreatedDate =
      typeof (initialData && initialData.createdDate) === 'string'
        ? new Date(initialData.createdDate)
        : (initialData && initialData.createdDate) || null;

    const rawType = (initialData && (initialData.type ?? initialData.typeId)) ?? null;

    const inferredFromLabel =
      !rawType && initialData && initialData.announcementType
        ? (TYPE_OPTIONS.find((o) => o.label === initialData.announcementType) || {}).id
        : null;

    setFormData({
      id: (initialData && initialData.id) ?? null,
      translationText: (initialData && initialData.translationText) ?? '',
      announcementType: (initialData && initialData.announcementType) ?? '',
      createdDate: fixedCreatedDate,
      createdName: (initialData && initialData.createdName) ?? '',
      type: rawType != null ? Number(rawType) : inferredFromLabel ?? null,
      isActive: (initialData && initialData.isActive) ?? 1,
      translations: mergedTranslations,
    });

    setLocalSubmitted(false);
    setErrors({});
  }, [visible, initialData, supportedLanguages, TYPE_OPTIONS]);

  const handleTranslationChange = (languageId, newText) => {
    setFormData((prev) => {
      const idx = prev.translations.findIndex((t) => t.languageId === languageId);
      const updated = [...prev.translations];
      if (idx >= 0) {
        updated[idx] = { ...updated[idx], text: newText };
      } else {
        updated.push({ languageId, text: newText });
      }

      const lang = supportedLanguages.find((l) => l.id === languageId);
      const langCode = lang && lang.code;
      if (langCode && errors['translation_' + langCode] && newText.trim()) {
        const next = { ...errors };
        delete next['translation_' + langCode];
        setErrors(next);
      }

      return { ...prev, translations: updated };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalSubmitted(true);

    const validationErrors = [];
    const newErrors = {};

    supportedLanguages.forEach((lang) => {
      const translation = formData.translations.find((t) => t.languageId === lang.id);
      const isRequired = lang.code === 'tr' || lang.code === 'en';
      if (isRequired && (!translation || !translation.text.trim())) {
        const msg = th('laborCatalog.form.translationRequired', { language: lang.name });
        validationErrors.push(msg);
        newErrors['translation_' + lang.code] = msg;
      }
    });

    setErrors(newErrors);

    if (validationErrors.length > 0) {
      validationErrors.forEach((detail, index) => {
        setTimeout(() => {
          toast.current?.show({
            severity: 'error',
            summary: th('laborCatalog.messages.missingField'),
            detail,
            life: 4000,
          });
        }, index * 200);
      });
      return;
    }

    const payload = {
      ...formData,
      type: formData.type != null ? Number(formData.type) : null,
      translations: formData.translations.map((t) => ({
        languageId: t.languageId,
        text: (t.text || '').trim(),
      })),
    };

    onSubmit(payload);
  };

  const renderFooter = () => {
    const canDelete = !!(formData && formData.id);
    return (
      <div>
        {canDelete && (
          <Button
            label={th('common.delete')}
            icon="pi pi-trash"
            severity="danger"
            type="button"
            onClick={() => handleDelete && handleDelete(formData.id)}
            disabled={loading}
          />
        )}
        <Button
          label={th('common.cancel')}
          icon="pi pi-times"
          onClick={onHide}
          outlined
          type="button"
        />
        <Button
          label={th('common.save')}
          icon="pi pi-check"
          onClick={handleSubmit}
          loading={!!loading}
        />
      </div>
    );
  };

  return (
    <Dialog
      visible={visible}
      footer={renderFooter()}
      style={{ width: '25vw' }}
      header={th('announcements.editTitle')}
      modal
      className="p-fluid"
      onHide={onHide}
      key={(formData && formData.id) ?? 'new'}
    >
      <Toast ref={toast} />
      <div className="field col-12">
        <form onSubmit={handleSubmit} className="p-fluid">
          <div className="flex flex-column gap-3">
            {/* Type alanı */}
            <div className="flex items-center gap-3">
              <label className="font-bold w-40 text-right" htmlFor="type">
                {th('announcements.formLabels.type')}
              </label>
              <div className="flex flex-wrap gap-3">
                {TYPE_OPTIONS.map((opt) => (
                  <div className="flex items-center" key={opt.id}>
                    <RadioButton
                      inputId={`type-${opt.id}`}
                      name="type"
                      value={opt.id}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          type: Number(e.value),
                        }))
                      }
                      checked={Number(formData.type) === Number(opt.id)}
                    />
                    <label htmlFor={`type-${opt.id}`} className="ml-2 cursor-pointer">
                      {opt.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* AnnouncementType (metin) alanı */}
            <div className="flex items-center gap-3">
              <label htmlFor="announcementType" className="font-bold w-40 text-right">
                {th('announcements.formLabels.announcementType')}
              </label>
            
            </div>

            {/* Çeviri alanları */}
            <div className="grid">
              {supportedLanguages.map((lang) => {
                const translation = formData.translations.find(
                  (t) => t.languageId === lang.id
                );
                const currentText = (translation && translation.text) || '';
                const isRequired = lang.code === 'tr' || lang.code === 'en';
                const inputId = `translation_text_${lang.code}`;
                const hasError = !!errors['translation_' + lang.code];

                return (
                  <div className="col-12" key={lang.id}>
                    <div className="field">
                      <label htmlFor={inputId}>
                        <span
                          className={getFlagClassNameForDialog(lang.code)}
                          style={{ fontSize: '1.2em', marginRight: '0.5em' }}
                          title={lang.name}
                        />
                        {lang.name} {isRequired && <span className="text-red-500">*</span>}
                      </label>
                      <InputText
                        id={inputId}
                        value={currentText}
                        onChange={(e) => handleTranslationChange(lang.id, e.target.value)}
                        className={
                          localSubmitted && (hasError || (isRequired && !currentText.trim()))
                            ? 'p-invalid'
                            : ''
                        }
                      />
                      {localSubmitted && (hasError || (isRequired && !currentText.trim())) && (
                        <small className="p-error">
                          {errors['translation_' + lang.code] ||
                            th('laborCatalog.form.translationRequired', {
                              language: lang.name,
                            })}
                        </small>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default AnnouncementTypeForm;
